<template>
  <v-container fluid>
    <Loading v-if="!loaded" :loading="true" :text="$t('deliveryDetail.loading')" />

    <v-alert v-else-if="accessDenied">
      {{ $t('errors.accessDenied') }}.
    </v-alert>

    <div v-else>
      <v-row>
        <v-col>
          <DeliveryCard
            :item="model.data"
            :metadata="model.metadata"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <!-- ---- card Leverancedetaljer ---- -->
          <v-card elevation="2">
            <v-toolbar flat>
              <v-toolbar-title>
                {{ $t('deliveryDetail.details.title') }}
              </v-toolbar-title>

              <v-spacer />

              <DeliveryEditDialog
                v-if="user.organizationKind == 'Internal'"
                :model="model"
                @saved="init"
              />
            </v-toolbar>
            <v-card-text>
              <v-row>
                <v-col cols="3">
                  <v-simple-table>
                    <tbody>
                      <tr>
                        <th>{{ $tc('common.location') }}</th>
                        <td class="text-right">
                          <LocationLink :id="model.data.locationId" />
                        </td>
                      </tr>
                      <tr>
                        <th>{{ $t('deliveryDetail.code') }}</th>
                        <td class="text-right">
                          {{ model.data.code }}
                        </td>
                      </tr>
                      <tr v-if="model.data.sourceTaskRequestId">
                        <th>{{ $t('deliveryDetail.sourceTaskRequestId') }}</th>
                        <td class="text-right">
                          <router-link :to="`/taskrequest/${model.data.sourceTaskRequestId}`">
                            {{ model.data.sourceTaskRequestCodeAndTitle }}
                          </router-link>
                        </td>
                      </tr>    
                      <tr>
                        <th>{{ $t('deliveryDetail.referenceInformations', { customerName: model.data.customerInitials }) }}</th>
                        <td class="text-right">
                          {{ model.data.referenceInformations.map(x => x.value).join(', ') }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t('deliveryDetail.externalReference') }}
                        </th>
                        <td class="text-right">
                          {{ model.data.externalReference }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col cols="5">
                  <v-simple-table>
                    <tbody>
                      <tr
                        v-for="(r,i) in model.data.responsibles"
                        :key="i"
                      >
                        <th class="d-flex align-center">
                          <v-icon left>mdi-account-outline</v-icon>
                          {{ r.responsibilityTitle }}
                        </th>
                        <td>
                          <span
                            v-if="r.personId"
                            class="text-caption"
                          >{{ r.personFullName }} ({{ r.personOrganizationInitials }})</span>
                        </td>
                        <td class="text-right">
                          <DeliveryResponsibleDialog
                            v-if="!!r.userCanEdit"
                            :title="r.responsibilityTitle"
                            :responsibility-id="r.responsibilityId"
                            @update:responsible="init"
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>
                          <v-icon left>mdi-account-multiple-outline</v-icon>
                          {{ $tc('common.follower', 2) }}
                        </th>
                        <td class="text-caption">
                          {{ $tc('deliveryDetail.followers', model.data.followerPersons.length) }}
                        </td>
                        <td class="text-right">
                          <DeliveryFollowersDialog @update:followers="init" />
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
                <v-col>
                  <v-simple-table>
                    <tbody>
                      <tr v-if="model.data.waitingForAction">
                        <th><v-icon>mdi-timer-sand</v-icon>{{ $t('deliveryDetail.waitingForAction') }}</th>
                        <td>{{ model.data.waitingForAction }}</td>
                      </tr>
                      <tr>
                        <th>{{ $t('deliveryDetail.deliveryDescriptionApprovedOn') }}</th>
                        <td>
                          <ValueState
                            :actual="model.data.deliveryDescriptionApprovedOn"
                            :expected="model.data.expectedDeliveryDescriptionApprovedOn"
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>{{ $t('deliveryDetail.partsReadyForShipmentOn') }}</th>
                        <td>
                          <ValueState
                            :actual="model.data.partsReadyForShipmentOn"
                            :expected="model.data.expectedPartsReadyForShipmentOn"
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>{{ $t('deliveryDetail.workStartedOn') }}</th>
                        <td>
                          <ValueState
                            :actual="model.data.workStartedOn"
                            :expected="model.data.expectedWorkStartedOn"
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>{{ $t('deliveryDetail.workEndedOn') }}</th>
                        <td>
                          <ValueState
                            :actual="model.data.workEndedOn"
                            :expected="model.data.expectedWorkEndedOn"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          lg="7"
          xl="8"
        >
          <v-row>
            <v-col>
              <Approval
                :is-closed="isClosed"
                :relation-kind="'delivery'"
                :relation-id="model.data.id"
                :customer-id="model.data.customerId"
                @create:success="init()"
              />
            </v-col>
          </v-row>

          <v-row v-if="user.organizationKind == 'Internal'">
            <v-col>
              <SpecificationComponent
                @loaded="specIds = $event"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <!-- ---- leverance aktiviteter ---- -->
              <v-card elevation="2">
                <v-card-title>
                  {{ $t('deliveries.activities.title') }}
                  <div class="flex-grow-1" />
                  <DeliveryActivityUpsertDialog
                    v-if="user.organizationKind == 'Internal'"
                    :show-create-button="true"
                    @saved="activitySaved()"
                  />
                </v-card-title>
                <v-card-text>
                  <DeliveryActivityList
                    ref="activities"
                    :delivery-id="model.data.id"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <!-- ---- card Leveranceoplysninger ---- -->
              <v-card elevation="2">
                <v-card-title>
                  {{ $t('deliveryDetail.info.title') }}
                </v-card-title>
                <v-card-text>
                  <span v-html="model.data.descriptionHtml" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <v-col>
          <ActivityLog
            :title="$t('deliveryDetail.activityLogTitle')"
            :add-endpoint="`delivery/${$route.params.id}/entry`"
            :relationId="$route.params.id"
            relationKind="Delivery"
            :entry-template="model.data.entryTemplate"
            :entries="model.data.entries"
            :closed="!!model.data.closedOn"
          />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import ActivityLog from '@/components/ActivityLog.vue';
import DeliveryCard from '@/components/delivery/DeliveryCard'
import Approval from '@/components/approval/Approval.vue'
import DeliveryActivityList from '@/components/delivery/DeliveryActivityList.vue'
import DeliveryActivityUpsertDialog from '@/components/delivery/DeliveryActivityUpsertDialog.vue'
import ValueState from '@/components/ValueState.vue';
import LocationLink from '@/components/LocationLink.vue';
import Loading from '@/components/Loading.vue';
import DeliveryResponsibleDialog from '@/components/delivery/DeliveryResponsibleDialog'
import DeliveryFollowersDialog from '@/components/delivery/DeliveryFollowersDialog'
import DeliveryEditDialog from '@/components/delivery/DeliveryEditDialog'
import SpecificationComponent from '@/components/specification/SpecificationComponent'

export default {  
  name: 'DeliveryDetail',
  components: {
    ActivityLog,
    DeliveryCard,
    ValueState,
    Approval,
    DeliveryActivityList,
    LocationLink,
    DeliveryActivityUpsertDialog,
    Loading,
    DeliveryResponsibleDialog,
    DeliveryFollowersDialog,
    DeliveryEditDialog,
    SpecificationComponent
  },
  beforeRouteLeave(to, from, next) {
    if (this.model?.data?.entryTemplate?.text !== '') {
      const answer = window.confirm(this.$t('feedback.confirmLeave'));

      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  data() {
    return {
      model: Object,
      detailMenu: false,
      specIds: [],
      accessDenied: false,
      //view only
      loaded: false,
      t: this.$t
    }
  },
  computed: {
    user() {
      return this.$store.getters.currentUser;
    },
    isClosed() {
      return !!this.model.data?.closedOn;
    }
  },
  watch: {
    '$route.params.id': {
      handler: function() {
        this.init();
      }
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.detailMenu = false;

      try {
        const { data } = await this.$api.get(`delivery/detail/${this.$route.params.id}`);

        if (data.success) {
          this.model = data.model;
        }

        else if (data.message === 'ERR_INCORRECT_CUSTOMER') {
          // If user has no other customer, display an error
          if (!this.$store.getters.userHasMultipleCustomers) {
            this.accessDenied = true;
            return;
          }

          // Try to find the correct customer ID
          const customerId = data.errors.find(x => this.isGuid(x.property))?.property;

          if (customerId) {
            this.$store.dispatch('setPrimaryCustomerId', customerId)
            this.init() // Calls itself. Be aware of inifite loop.
          }

          else {
            this.$store.dispatch('clearPrimaryCustomer');
          }
        }

      } finally {
        this.loaded = true;
      }
    },
    activitySaved: function () {
      this.$refs.activities.reload();
    },
    isGuid(guid) {
      const regex = /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/g;
      return regex.test(guid);
    }
  }
}
</script>